import { type FC, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface MarkdownRendererProps {
  content: string;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}

function extractText(node: React.ReactNode): string {
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (!node) return "";
  if (Array.isArray(node)) return node.map(extractText).join("");
  if (typeof node === "object" && "props" in node) {
    return extractText(
      (node as { props: { children?: React.ReactNode } }).props.children,
    );
  }
  return "";
}

function makeHeading(Tag: string) {
  return function HeadingWithId({
    children,
    ...props
  }: React.HTMLAttributes<HTMLHeadingElement> & {
    children?: React.ReactNode;
  }) {
    const id = slugify(extractText(children));
    return (
      // @ts-expect-error dynamic tag
      <Tag id={id} {...props}>
        {children}
      </Tag>
    );
  };
}

const DOCUMENT_CSS = `
  *,*::before,*::after{box-sizing:border-box}
  body{
    font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;
    font-size:14px;
    line-height:1.75;
    color:#000;
    margin:0;
    padding:40px;
    background:#fff;
    -webkit-font-smoothing:antialiased;
  }
  h1{font-size:1.25rem;font-weight:500;margin:0 0 1rem}
  h2{font-size:1.125rem;font-weight:500;margin:1.5rem 0 0.75rem}
  h3{font-size:1rem;font-weight:500;margin:1rem 0 0.5rem}
  h4,h5,h6{font-size:0.875rem;font-weight:500;margin:1rem 0 0.5rem}
  p{margin:0 0 0.75rem}
  p:last-child{margin-bottom:0}
  ul{list-style:disc;margin:0.5rem 0;padding-left:1.5rem}
  ol{list-style:decimal;margin:0.5rem 0;padding-left:1.5rem}
  li{margin:0.125rem 0}
  a{color:#2563eb;text-decoration:underline}
  a:hover{opacity:0.8}
  blockquote{border-left:2px solid #d4d4d4;padding-left:0.75rem;font-style:italic;color:#525252;margin:0.5rem 0}
  code{background:#f5f5f5;padding:0.125rem 0.25rem;border-radius:0.25rem;font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,monospace;font-size:0.75rem}
  pre{background:#f5f5f5;padding:0.75rem;border-radius:0.5rem;overflow-x:auto;margin:0.5rem 0;font-size:0.75rem}
  pre code{background:none;padding:0}
  hr{border:none;border-top:1px solid #e5e5e5;margin:1rem 0}
  table{width:100%;border-collapse:collapse;margin:0.5rem 0;font-size:0.875rem}
  th{border:1px solid #e5e5e5;background:#f5f5f5;padding:0.375rem 0.5rem;text-align:left}
  td{border:1px solid #e5e5e5;padding:0.375rem 0.5rem}
  strong{font-weight:600}
  em{font-style:italic}
  img{max-width:100%;height:auto}
`;

const SHELL_HTML = `<!DOCTYPE html><html><head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<style>${DOCUMENT_CSS}</style>
</head><body></body></html>`;

function AnchorLink({
  href,
  children,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  children?: React.ReactNode;
}) {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!href) return;

    let targetId: string | null = null;
    if (href.startsWith("#")) {
      targetId = href.slice(1);
    } else {
      try {
        const parsed = new URL(href, "http://placeholder");
        if (parsed.hash) targetId = parsed.hash.slice(1);
      } catch {
        /* malformed URL — fall through */
      }
    }

    if (targetId) {
      e.preventDefault();
      const doc = e.currentTarget.ownerDocument;
      const target = doc.getElementById(targetId);
      if (!target) return;

      const iframeEl = doc.defaultView?.frameElement as HTMLElement | null;
      if (!iframeEl) return;

      const scrollContainer = findScrollContainer(iframeEl);
      if (!scrollContainer) return;

      const containerRect = scrollContainer.getBoundingClientRect();
      const iframeRect = iframeEl.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();

      scrollContainer.scrollTo({
        top:
          scrollContainer.scrollTop +
          (iframeRect.top - containerRect.top) +
          targetRect.top -
          16,
        behavior: "smooth",
      });
      return;
    }

    e.preventDefault();
    const parentWindow =
      e.currentTarget.ownerDocument.defaultView?.parent ?? window;
    parentWindow.open(href, "_blank", "noopener,noreferrer");
  };

  return (
    <a href={href} onClick={handleClick} {...props}>
      {children}
    </a>
  );
}

function findScrollContainer(el: HTMLElement): HTMLElement | null {
  let current = el.parentElement;
  while (current) {
    const { overflow, overflowY } = getComputedStyle(current);
    if (
      overflow === "auto" ||
      overflow === "scroll" ||
      overflowY === "auto" ||
      overflowY === "scroll"
    ) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

const markdownComponents = {
  h1: makeHeading("h1") as never,
  h2: makeHeading("h2") as never,
  h3: makeHeading("h3") as never,
  h4: makeHeading("h4") as never,
  h5: makeHeading("h5") as never,
  h6: makeHeading("h6") as never,
  a: AnchorLink as never,
};

const remarkPlugins = [remarkGfm];

/**
 * Renders markdown inside an iframe via createPortal so anchor links scroll
 * within the document and all navigation stays contained.
 * Uses createPortal instead of react-dom/server to stay browser-compatible.
 */
export const MarkdownRenderer: FC<MarkdownRendererProps> = ({ content }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [mountTarget, setMountTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const handleLoad = () => {
      const body = iframe.contentDocument?.body;
      if (body) setMountTarget(body);
    };

    iframe.addEventListener("load", handleLoad);
    if (iframe.contentDocument?.readyState === "complete") {
      handleLoad();
    }
    return () => iframe.removeEventListener("load", handleLoad);
  }, []);

  useEffect(() => {
    if (!mountTarget) return;
    const iframe = iframeRef.current;
    if (!iframe) return;

    const resize = () => {
      iframe.style.height = `${mountTarget.scrollHeight}px`;
    };

    const mo = new MutationObserver(resize);
    mo.observe(mountTarget, { childList: true, subtree: true, characterData: true });

    const ro = new ResizeObserver(resize);
    ro.observe(mountTarget);

    resize();

    return () => {
      mo.disconnect();
      ro.disconnect();
    };
  }, [mountTarget]);

  if (!content) {
    return (
      <div className="cuadra-p-4 cuadra-text-sm cuadra-text-muted-foreground">
        No content
      </div>
    );
  }

  return (
    <div className="cuadra-py-[4%] cuadra-px-[5%]">
      <div className="cuadra-bg-white dark:cuadra-bg-[#f5f5f4] cuadra-rounded-lg cuadra-shadow-sm cuadra-border cuadra-border-[rgba(0,0,0,0.06)] dark:cuadra-border-white/10 cuadra-overflow-hidden">
        <iframe
          ref={iframeRef}
          srcDoc={SHELL_HTML}
          title="Markdown document"
          className="cuadra-w-full cuadra-border-0"
          style={{ minHeight: "200px" }}
        />
        {mountTarget &&
          createPortal(
            <ReactMarkdown
              remarkPlugins={remarkPlugins}
              components={markdownComponents}
            >
              {content}
            </ReactMarkdown>,
            mountTarget,
          )}
      </div>
    </div>
  );
};
